import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  OverviewContainer,
  Label,
} from "../Marketing.styled";

import {
  AdvancedSettingsCardInfo,
  BoxLabel,
  UserSelectorWrapper,
  HumanWrapper,
  CustomSelectorWrapper,
  RadioWrapper,
  AssignmentContainer,
  StyledSwitch,
  CustomGenericListStyle,
  ContactOwnerWrapper,
  RoundRobinWrapper,
  RoundRobinSwitchesContainer,
  BoxHeader,
  BoxQty,
  AdvancedSettingsContainer
} from "./AdvancedSettings.styled";
import UserSelector from "../../../components/UserSelector/Selector/UserSelector";
import UserDisplay from "../../../components/UserSelector/Display/UserDisplay";
import MultiSelector from "../../../components/UserSelector/MultiSelector/MultiSelector";
import SimpleCard from "../../../components/SimpleCard";
import FormControlLabel from '@mui/material/FormControlLabel';

import { useStore } from "../../../../../../hooks";
import { observer } from "mobx-react";
import {useMediaQueries} from "../../../../../../utils/mediaQueries";
import {AdvancedSettingsAssignmentTypes} from "../../../../../../utils/enums";
import Spinner from "../../../../../../components/common/Spinner";
import Alerts from "../../../../../../elements/v2/Alerts/Alerts";
import {useUserCheckPermissions} from "../../../../../../hooks/useUserCheckPermissions";
import {PermissionCodes} from "../../../../../../utils/enums";

const AdvancedSettings = observer(() => {
  const { authStore, marketingStore } = useStore();
  const { advancedSettingsStore } = marketingStore;
  const { isMobile } = useMediaQueries();
  const { checkPermission } = useUserCheckPermissions();

  if (!marketingStore.currentCampaign) {
    return <Spinner />;
  }

  const selectedUser = useMemo(() => {
    if (!advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.SINGLE]) return {};
    return advancedSettingsStore.selectedUser || {};
  }, [advancedSettingsStore.selectedUser, advancedSettingsStore.selectedAssignmentTypes]);

  const handleAssignmentChange = (event) => {
    const assignmentType = event.target.value;
    const isChecked = event.target.checked;

    advancedSettingsStore.handleAssignmentChange(
      assignmentType,
      isChecked,
      marketingStore.customer
    );
  };

  const handleSingleUserSelect = useCallback((userId) => {
    if (advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.SINGLE]) {
      advancedSettingsStore.setSelectedUser(userId);
    }
  }, [advancedSettingsStore]);

  /**
   * Generic handler for multiple selections in Round Robin
   * Handles users, groups, and roles with a single callback
   * @param {string[]} ids - Selected IDs
   * @param {string} type - Selection type: 'user', 'group', or 'role'
   */
  const handleMultiSelect = (ids, type) => {
    if (!advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CUSTOM]) return;

    const actionMap = {
      user: () => advancedSettingsStore.setSelectedUserIds(ids),
      group: () => advancedSettingsStore.setSelectedGroupIds(ids),
      role: () => advancedSettingsStore.setSelectedRoleIds(ids)
    };

    actionMap[type]?.();
  };

  useEffect(() => {
    if (authStore.selectedAccount?.id) {
      advancedSettingsStore.loadAllRequiredData(authStore.selectedAccount.id);
    }
  }, [authStore.selectedAccount?.id]);

  useEffect(() => {
    if (marketingStore.owner && advancedSettingsStore.usersItems.length > 0) {
      advancedSettingsStore.setDefaultUser(marketingStore.owner);
    }
  }, [marketingStore.owner, advancedSettingsStore.usersItems]);

  return (
    <>
      {checkPermission(PermissionCodes.HITL, "UPDATE") && (
        <>
          <CustomGenericListStyle/>

          <AdvancedSettingsContainer isMobile={isMobile}>
            <HumanWrapper>
              <Label>Human in the loop</Label>

              <AdvancedSettingsCardInfo className="contactOwner">
                <AssignmentContainer>
                  <BoxLabel order={1}>Contact Owner</BoxLabel>
                  <RadioWrapper>
                    <FormControlLabel
                        value={AdvancedSettingsAssignmentTypes.CONTACT}
                        control={
                          <StyledSwitch
                              checked={advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CONTACT]}
                              onChange={handleAssignmentChange}
                              value={AdvancedSettingsAssignmentTypes.CONTACT}
                          />
                        }/>
                  </RadioWrapper>
                </AssignmentContainer>
                <ContactOwnerWrapper>
                  {advancedSettingsStore.isLoadingRoles ? (
                      <Spinner />
                  ) : (
                      advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CONTACT] && (
                          <div className="simple-card-container">
                            {advancedSettingsStore.rolesItems.map(role => (
                                <SimpleCard
                                    key={role.id}
                                    title={role.name}
                                    description=""
                                    checked={advancedSettingsStore.selectedContactOwnerRoles.includes(role.id)}
                                    onChange={() => advancedSettingsStore.handleContactOwnerRoleSelect(role.id)}
                                    value={role.id}
                                />
                            ))}
                          </div>
                      )
                  )}
                </ContactOwnerWrapper>
              </AdvancedSettingsCardInfo>

              {checkPermission(PermissionCodes.DEFAULT_ASSIGNMENT, "UPDATE") && (
                  <AdvancedSettingsCardInfo className="defaultUser">
                <AssignmentContainer>
                  <BoxLabel order={1}>Default assigned user (Campaign Creator)</BoxLabel>
                  <RadioWrapper>
                    <FormControlLabel
                        value={AdvancedSettingsAssignmentTypes.DEFAULT}
                        control={
                          <StyledSwitch
                              checked={advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.DEFAULT]}
                              onChange={handleAssignmentChange}
                              value={AdvancedSettingsAssignmentTypes.DEFAULT}
                              disabled={!advancedSettingsStore.defaultUser}
                          />
                        }/>
                  </RadioWrapper>
                </AssignmentContainer>
                {advancedSettingsStore.isLoadingUsers ? (
                  <Spinner />
                ) : (
                  <UserDisplay defaultUser={advancedSettingsStore?.defaultUser} displayPhoto={false}/>
                )}
              </AdvancedSettingsCardInfo>
              )}

              {checkPermission(PermissionCodes.SPECIFIC_USER, "UPDATE") &&
                  <AdvancedSettingsCardInfo className="assignUser">
                <AssignmentContainer>
                  <BoxLabel order={1}>Assign a User</BoxLabel>
                  <RadioWrapper>
                    <FormControlLabel
                        value={AdvancedSettingsAssignmentTypes.SINGLE}
                        control={
                          <StyledSwitch
                              checked={advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.SINGLE]}
                              onChange={handleAssignmentChange}
                              value={AdvancedSettingsAssignmentTypes.SINGLE}
                          />
                        }
                    />
                  </RadioWrapper>
                </AssignmentContainer>
                <UserSelectorWrapper $isMobile={isMobile}>
                  {advancedSettingsStore.isLoadingUsers ? (
                    <Spinner />
                  ) : (
                    <UserSelector
                        selectedUser={selectedUser}
                        onChange={handleSingleUserSelect}
                        customerId={authStore.selectedAccount?.id}
                    />
                  )}
                </UserSelectorWrapper>
              </AdvancedSettingsCardInfo>
              }

              {checkPermission(PermissionCodes.ROUND_ROBIN, "UPDATE") && (
                <AdvancedSettingsCardInfo className="roundRobin">
                  <RoundRobinSwitchesContainer>
                    <SimpleCard
                        title="Round robin"
                        description="Distribute interactions evenly across team members"
                        checked={advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CUSTOM]}
                        onChange={handleAssignmentChange}
                        value={AdvancedSettingsAssignmentTypes.CUSTOM}
                    />

                    <SimpleCard
                        title="Sticky assignment"
                        description="Always assign to the same user if conversation is reopened"
                        checked={advancedSettingsStore.stickyAssignment}
                        onChange={(e) => {
                          advancedSettingsStore.setStickyAssignment(e.target.checked);
                        }}
                        value="sticky"
                        disabled={!advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CUSTOM]}
                    />
                  </RoundRobinSwitchesContainer>

                  {advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.CUSTOM] && (
                    <RoundRobinWrapper>
                      <CustomSelectorWrapper>
                        <AssignmentContainer>
                          <BoxHeader>
                            <BoxLabel>Users</BoxLabel>
                            <BoxQty>Assigned Users: {advancedSettingsStore.selectedUserIds?.length || 0}</BoxQty>
                          </BoxHeader>
                          <RadioWrapper>
                            <FormControlLabel
                              control={
                                <StyledSwitch
                                  checked={advancedSettingsStore.userSelectorEnabled}
                                  onChange={(e) => {
                                    advancedSettingsStore.setSelectorEnabled('user', e.target.checked);
                                  }}
                                />
                              }
                            />
                          </RadioWrapper>
                        </AssignmentContainer>

                        <MultiSelector
                          selectedUserIds={advancedSettingsStore.selectedUserIds || []}
                          onChange={(ids) => handleMultiSelect(ids, 'user')}
                          customerId={authStore.selectedAccount?.id}
                          placeholder="Search and Select Users"
                          filterType="user"
                          label="Select Users"
                          disabled={!advancedSettingsStore.userSelectorEnabled || advancedSettingsStore.isLoadingUsers}
                          isLoading={advancedSettingsStore.isLoadingUsers}
                        />
                      </CustomSelectorWrapper>

                      <CustomSelectorWrapper>
                        <AssignmentContainer>
                          <BoxHeader>
                            <BoxLabel>Roles</BoxLabel>
                            <BoxQty>Assigned Roles: {advancedSettingsStore.selectedRoleIds?.length || 0}</BoxQty>
                          </BoxHeader>
                          <RadioWrapper>
                            <FormControlLabel
                                control={
                                  <StyledSwitch
                                      checked={advancedSettingsStore.roleSelectorEnabled}
                                      onChange={(e) => {
                                        advancedSettingsStore.setSelectorEnabled('role', e.target.checked);
                                      }}
                                  />
                                }
                            />
                          </RadioWrapper>
                        </AssignmentContainer>
                        <MultiSelector
                            selectedUserIds={advancedSettingsStore.selectedRoleIds || []}
                            onChange={(ids) => handleMultiSelect(ids, 'role')}
                            customerId={authStore.selectedAccount?.id}
                            placeholder="Search and Select Roles"
                            filterType="role"
                            label="Select Roles"
                            disabled={!advancedSettingsStore.roleSelectorEnabled || advancedSettingsStore.isLoadingRoles}
                            isLoading={advancedSettingsStore.isLoadingRoles}
                        />
                      </CustomSelectorWrapper>

                      <CustomSelectorWrapper>
                        <AssignmentContainer>
                          <BoxHeader>
                            <BoxLabel>Groups</BoxLabel>
                            <BoxQty>Assigned Groups: {advancedSettingsStore.selectedGroupIds?.length || 0}</BoxQty>
                          </BoxHeader>
                          <RadioWrapper>
                            <FormControlLabel
                              control={
                                <StyledSwitch
                                  checked={advancedSettingsStore.groupSelectorEnabled}
                                  onChange={(e) => {
                                    advancedSettingsStore.setSelectorEnabled('group', e.target.checked);
                                  }}
                                />
                              }
                            />
                          </RadioWrapper>
                        </AssignmentContainer>
                        <MultiSelector
                          selectedUserIds={advancedSettingsStore.selectedGroupIds || []}
                          onChange={(ids) => handleMultiSelect(ids, 'group')}
                          customerId={authStore.selectedAccount?.id}
                          placeholder="Search and Select Groups"
                          filterType="group"
                          label="Select Groups"
                          disabled={!advancedSettingsStore.groupSelectorEnabled || advancedSettingsStore.isLoadingGroups}
                          isLoading={advancedSettingsStore.isLoadingGroups}
                        />
                      </CustomSelectorWrapper>
                    </RoundRobinWrapper>
                  )}
                </AdvancedSettingsCardInfo>
              )}
            </HumanWrapper>
          </AdvancedSettingsContainer>
        </>
      )}

      {advancedSettingsStore.isError && (
        <Alerts
          id="advanced-settings-error"
          message={advancedSettingsStore.errorMessage}
          severity="error"
          onClose={() => advancedSettingsStore.setIsError(false)}
        />
      )}
    </>
  );
});

export default AdvancedSettings;
